// L6 fix round — the backend's denial-reason enum (swiftpass/amenities/models/constants.py:85-88)
// mapped to admin-facing copy. QUOTA/CUTOFF are currently never emitted by the backend and have no
// established product meaning yet (a review found no code-level link to any lead-time/hours concept
// — that was a positional guess in an earlier mockup) — labeled literally and neutrally from the
// code itself rather than invented, pending a backend follow-up. LEAD_TIME/MAX_ADVANCE/OUTSIDE_HOURS
// were added by the backend in 4e2bae8b and describe what the resident actually hit: booked closer
// to the start than the amenity's lead time allows, booked further out than its max advance window,
// or requested a slot outside its configured operating hours.
const DENIAL_REASON_LABELS: Record<string, string> = {
    BOOKING_DENIED_FULL: 'Slot was full',
    BOOKING_DENIED_QUOTA: 'Booking quota reached',
    BOOKING_DENIED_CUTOFF: 'Past booking cutoff',
    BOOKING_DENIED_SUSPENDED: 'Unit suspended',
    BOOKING_DENIED_LEAD_TIME: 'Booked too close to start',
    BOOKING_DENIED_MAX_ADVANCE: 'Booked too far ahead',
    BOOKING_DENIED_OUTSIDE_HOURS: 'Outside opening hours',
};

// `WORD_WORD` -> `Word Word` — the fallback for an unrecognized/future denial code, so a new
// backend enum member degrades to something readable instead of rendering blank or throwing.
const titleCaseWords = (value: string): string =>
    value
        .toLowerCase()
        .split('_')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

// Amenity Terms & Insurance (TECHSPEC_AMENITY_TERMS_AND_INSURANCE.md §2's state machine) — shared by
// the Approvals "Insurance" tab's state-chip filter, its queue rows, and the Bookings tab's
// per-booking chip, so the same booking always reads the same word wherever it's shown.
// `NOT_REQUIRED` never actually reaches a booking payload (D8) but is kept here so the lookup stays
// exhaustive against the backend's own enum.
const INSURANCE_STATE_LABELS: Record<string, string> = {
    NOT_REQUIRED: 'Not required',
    AWAITING_DOCUMENT: 'Awaiting document',
    AWAITING_REVIEW: 'Awaiting review',
    CHANGES_REQUESTED: 'Changes requested',
    APPROVED: 'Approved',
    DENIED: 'Denied',
};

export default {
    title: 'Amenities',
    newAmenity: 'New amenity',
    editAmenity: 'Edit amenity',
    // P2-C (polish round): the Settings wizard's first card used to reuse `title` ("Amenities" —
    // the module name, plural) as its own section header, which reads as a leftover label on the
    // settings page of a SINGLE amenity rather than a description of what the card holds. `title`
    // itself stays as-is — it's shared with the breadcrumb/page-title/module nav elsewhere.
    basics: 'Basics',
    // Unified amenity page (console redesign) — tab labels.
    overviewTab: 'Overview',
    settingsTab: 'Settings',
    // Task A (QA/Atul) — Deactivate ("archive") / Reactivate an amenity from the detail header.
    // Deliberately NOT labelled "Delete": setting status INACTIVE is a reversible off switch that
    // hides the amenity from residents and stops NEW bookings, while KEEPING existing future
    // bookings for admins to see and cancel. The confirm copy spells that out so no admin thinks
    // they are erasing bookings.
    deactivate: 'Deactivate amenity',
    reactivate: 'Reactivate amenity',
    deactivateConfirmTitle: 'Deactivate this amenity?',
    deactivateConfirmBody:
        'It will be hidden from residents and stop taking new bookings. Existing bookings are kept — you can still see and cancel them. You can reactivate it any time.',
    deactivateConfirmOk: 'Deactivate',
    deactivateSuccess: 'Amenity deactivated — hidden from residents and closed to new bookings.',
    reactivateConfirmTitle: 'Reactivate this amenity?',
    reactivateConfirmBody: 'Residents will see it again and can book new slots. Its existing bookings are unaffected.',
    reactivateConfirmOk: 'Reactivate',
    reactivateSuccess: 'Amenity reactivated — residents can book it again.',
    // P2-A: Overview's four static-config vitals cards (Hours/Doors/Cancellation policy/Booking
    // mode) collapsed into one "Setup" card, with a link back to where they're actually edited.
    overviewSetupCardTitle: 'Setup',
    overviewSetupEdit: 'Edit',
    // F3 (round-2 critique): "Next up today" now only lists slots with a real booking or an admin
    // block (see Detail/overviewHelpers.ts's slotWorthShowingOnOverview) — plain-English empty
    // state for when none remain, matching the Reports-page question-and-answer voice rather than
    // reading like a leftover system label.
    overviewNextUpEmpty: 'Nothing else booked today.',
    overviewNextUpLoadError: 'Could not load today’s bookings.',
    notEnabled: 'Amenity booking is not enabled for this account. Contact Swiftlane to enable it.',
    // P1-A (polish round): sticky bottom Save/Cancel bar, shown once the admin has genuinely
    // touched the form (Manage/index.tsx) — the wizard's own real Save is otherwise thousands of
    // pixels below Capacity/Booking rules/Door access/Photos/Hours. `unsavedChangesGuard` is the
    // in-app navigation-guard confirm (react-router `Prompt`) shown when the admin tries to switch
    // tabs/routes with unsaved edits still pending.
    unsavedChanges: 'Unsaved changes',
    unsavedChangesActionsLabel: 'Unsaved changes actions',
    unsavedChangesGuard: 'You have unsaved changes on this amenity. Leave without saving?',
    // F2 (round-2 critique): a blocked save on this ~2,500px form was silent from wherever the
    // admin was standing — the sticky bar kept reading "Unsaved changes" even after Save was
    // rejected. Replaces that label with the actual problem so the click that got swallowed also
    // gets explained; paired with scrolling + focusing the first invalid field (see
    // focusFirstInvalidField.ts).
    unsavedChangesFieldsNeedFixing: (n: number): string => (n === 1 ? '1 field needs fixing' : `${n} fields need fixing`),
    blockSlot: 'Block a slot',
    bookOnBehalf: 'Book for a resident',
    cancelBooking: 'Cancel booking',
    bookings: 'Bookings',
    exportCsv: 'Export CSV',
    name: 'Name',
    nameTooLong: 'Name must be 128 characters or fewer.',
    capacity: 'Capacity',
    slotMinutes: 'Slot length (minutes)',
    bufferMinutes: 'Buffer (minutes)',
    leadTime: 'Lead time (minutes)',
    maxAdvance: 'Max advance booking (days)',
    visibility: 'Visibility',
    visibilityAll: 'All residents',
    visibilityRestricted: 'Restricted to groups',
    // Photos (JSONB string[] on the amenity; first is the cover shown on the resident-facing card).
    photos: 'Photos',
    photosPlaceholder: 'https://…',
    photosCover: 'Cover',
    photosHint: 'Paste an image URL or upload an image file. The first photo is used as the cover on the resident-facing amenity card.',
    addPhoto: 'Add photo',
    removePhoto: 'Remove',
    uploadPhotoButton: 'Upload image',
    // QA item 4 (Atul, 31-Aug): "Added photos should be able to click and view in big pop-up. No
    // need to show the URL". `photoAlt` is the thumbnail's alt text — it replaces the previous
    // `alt=''` and doubles as the accessible name of the lightbox trigger (1-based, so the cover
    // photo reads "Photo 1"). `photoPreview` is the hover mask shown over a set thumbnail; antd's
    // own default mask copy comes from its locale bundle rather than this module, so it's
    // overridden at the call site to keep every user-facing string in `config/texts`.
    photoAlt: (position: number): string => `Photo ${position}`,
    photoPreview: 'View',
    photosInvalidUrl: 'Enter a valid image URL starting with http:// or https://, or upload an image.',
    hours: 'Hours',
    status: 'Status',
    amenityNotFound: 'Amenity not found',
    // P3-H: the previous copy ("This amenity doesn't exist, or hasn't loaded yet.") read as
    // implementation doubt with no path forward — this names the likely cause and pairs it with
    // two real actions (Back to amenities / Retry) instead.
    amenityNotFoundDesc: "We couldn't find this amenity. It may have been deleted.",
    backToAmenities: 'Back to amenities',
    amenityNotFoundRetry: 'Retry',
    amenityLoadError: "Couldn't load this amenity",
    addHours: '+ Add hours',
    copyToAllDays: 'Copy to all days',
    // P2-B (polish round): the Hours editor now leads with a "Same hours every day" toggle that
    // collapses all 7 days into one Mon-Sun row (the common case — see sameHoursEveryDay below),
    // with exactly ONE "Copy Monday to all days" control living in the section header rather than
    // a "Copy to all days" link repeated on every one of the 7 per-day rows.
    sameHoursEveryDay: 'Same hours every day',
    everyDayLabel: 'Mon–Sun',
    copyMondayToAllDays: 'Copy Monday to all days',
    nextDay: 'Next day',
    closed: 'Closed',
    date: 'Date',
    seats: 'Seats',
    seatsLeft: 'seats left',
    of: 'of',
    noSlots: 'No slots available for this day.',
    noBookings: 'No bookings for this slot.',
    cancelBookingConfirm: 'Are you sure you want to cancel this booking?',
    unit: 'Unit',
    block: 'Block',
    selectSlot: 'Slot',
    reason: 'Reason',
    reasonPlaceholder: 'e.g. Maintenance, private event',
    resident: 'Resident',
    slotBlocked: 'Slot blocked.',
    // Destructive-action warning shown in BlockSlotModal when the target slot is
    // already occupied: the backend `block_slot` cancels + fully refunds every booking
    // in the slot and emails each resident the reason. The admin must see this before
    // confirming. Seat-based (ISlot carries no booking list) — see bookedSeatsInSlot.
    blockOccupiedWarning: (seats: number): string =>
        `This slot has ${seats} booked seat${seats === 1 ? '' : 's'}. Blocking it will cancel every booking here, ` +
        'fully refund each payment, and email those residents your reason.',
    onBehalfBooked: 'Booking created.',
    minutesShort: 'min',
    windowSingular: 'window',
    windowPlural: 'windows',
    removeWindow: 'Remove window',
    nextDayOffset: '(+1 day)',
    // Hours-grid save-time gate: `days` is a pre-joined "Tuesday" / "Tuesday and Friday" list,
    // `plural` picks the matching verb.
    windowIncomplete: (days: string, plural: boolean): string =>
        `${days} ${plural ? 'have' : 'has'} an incomplete hours row — set both a start and end time, or remove it.`,
    windowsOverlap: (days: string, plural: boolean): string =>
        `${days} ${plural ? 'have' : 'has'} overlapping hours — merge or remove duplicates.`,
    reports: 'Reports',
    reportsComingSoon: 'Usage and demand reports for this amenity are coming soon.',
    suspensions: 'Unit suspensions',
    suspensionsComingSoon: 'Unit suspension management is coming soon.',
    // P2-G: explains the consequence of suspending a unit right under the page title, since the
    // action itself (blocking a resident from booking) isn't obvious from "Suspend a unit" alone.
    suspensionsSubtitle: 'Suspending a unit blocks its residents from booking any amenity until the suspension is lifted.',
    suspensionStatusFilter: 'Status',
    suspensionStatusAll: 'All statuses',
    // §6.4 admin unit suspensions.
    suspendUnit: 'Suspend a unit',
    // Empty-state for the Unit picker when the workspace has no units yet — points the admin at
    // where units actually come from instead of a bare antd "No data".
    noUnitsFound: 'No units found — units are managed under Sites & Access Points.',
    suspensionReason: 'Reason',
    suspensionReasonPlaceholder: 'e.g. Noise complaints, HOA hearing pending',
    suspensionReasonTooLong: 'Reason must be 255 characters or fewer.',
    suspensionEndDate: 'End date (optional)',
    suspensionIndefinite: 'Indefinite',
    suspensionStartsAt: 'Starts',
    suspensionEndsAt: 'Ends',
    suspensionCreated: 'Unit suspended.',
    suspensionLiftedMsg: 'Suspension lifted.',
    liftSuspension: 'Lift',
    liftSuspensionConfirm: 'Lift this suspension? The unit will be able to book amenities again.',
    suspensionStateActive: 'Active',
    suspensionStateScheduled: 'Scheduled',
    suspensionStateLifted: 'Lifted',
    noSuspensions: 'No unit suspensions yet.',
    affectedBookings: 'Affected bookings',
    affectedBookingsNote: "Cancelling a booking is a separate, deliberate action — do it from the amenity's Bookings page.",
    affectedBookingsEmpty: 'No upcoming bookings for this unit.',
    affectedBookingsWindow: 'When',
    // §6.1 access mode + mapped doors.
    // P2-C (polish round): was 'Access control' — collided with the sidebar's own top-level
    // "Access control" module, reading as if this card configured that whole module rather than
    // just this amenity's door behavior.
    accessControl: 'Door access',
    accessMode: 'Access mode',
    accessModeShared: 'Shared',
    accessModeSharedDesc: 'Existing door access works as usual; a booking is informational only.',
    accessModeBookingOnly: 'Booking only',
    accessModeBookingOnlyDesc: 'Only residents with a confirmed booking can enter the mapped doors during their window.',
    accessModeExclusive: 'Exclusive',
    accessModeExclusiveDesc: 'Only the booker and the staff group can enter the mapped doors during the booking window.',
    doorMapping: 'Mapped doors',
    staffGroup: 'Staff group (exempt from Exclusive denial)',
    // §6.2 config setters (guest passes + waitlist).
    // P2-C (polish round): was 'Booking configuration' — "configuration" is system vocabulary; the
    // section is really the resident-facing rules governing a booking (grace, guest passes,
    // waitlist), so "Booking rules" says what it does in plain English.
    bookingConfig: 'Booking rules',
    // D1 (backend PR #3088). The API spelling is a plain `requires_approval` boolean; the column
    // behind it (`approval_mode`, AUTO|MANUAL) is never exposed. Deliberately labelled as the thing
    // it does to a resident's booking, not as a mode name.
    requiresApproval: 'Require approval',
    graceMinutes: 'Grace period (minutes)',
    graceMinutesHint: 'Extends door access before and after each booking window.',
    maxGuestPins: 'Guest passes per booking',
    maxGuestPinsHint: 'Set to 0 to disable guest passes for this amenity.',
    requireGuestNames: 'Require a name for each guest pass',
    waitlistEnabled: 'Enable waitlist',
    waitlistMaxPerSlot: 'Waitlist limit per slot',
    waitlistMaxPerSlotHint: 'How many residents can wait in line for a full slot. Leave empty to use capacity.',
    waitlistMaxPerSlotPlaceholder: 'Defaults to capacity',
    // Pricing card (console redesign, Piece A — design-amenity-payments-ui.md): sits directly above
    // Cancellation policy, since that editor charges a percentage of THIS price — before this card
    // existed there was no way to set it at all. `price` carries the field's own always-visible
    // inline hint (priceHint), so it deliberately gets no separate FieldHelp ⓘ icon — same pattern
    // as graceMinutes/maxGuestPins/waitlistMaxPerSlot above (P3-D).
    pricing: 'Pricing',
    // Payment-gate batch (2026-09-28): the "This amenity is chargeable" toggle Manage/pricingGate.ts
    // gates — a FORM-ONLY control, deriving its initial value from `price_cents > 0` on load (no new
    // stored field: `price_cents == 0` is already the free sentinel in four other places, see
    // shouldShowPaymentsDisabledNotice's own doc comment for the sibling company-wide gate). Turning
    // it on requires the property to actually be able to collect (resolvePricingGate) — see the
    // pricingGate* copy below for the locked explanation.
    chargeableLabel: 'This amenity is chargeable',
    chargeableHint: 'Residents pay the price below when they book. Turn this off for a free amenity.',
    // The always-visible "visibly locked" cue next to the switch (not a tooltip — a disabled
    // control's tooltip is unreachable by keyboard and invisible on touch, same reasoning as
    // AmenityPayments/List/SetupChoiceCards.tsx's oauthUnavailableReason). The FULLER explanation +
    // remedy live in the panel (pricingGateMessage below), revealed only once the admin actually
    // tries to turn the toggle on (or it was already on from a legacy price) — this tag is just the
    // at-a-glance signal that something needs fixing first.
    chargeableLockedTag: 'Payments not set up',
    price: 'Price (USD)',
    priceHint: 'The amount charged to a resident when they book.',
    // Per-seat pricing (2026-10-12): what the price is the price OF. Backend `pricing_mode` —
    // PER_BOOKING (default) charges the price once per booking; PER_SEAT charges price × seats.
    pricingModeLabel: 'Charge',
    pricingModePerBooking: 'Per booking',
    pricingModePerSeat: 'Per seat',
    pricingModeHint: 'Per seat charges the price for every seat a resident books — 3 seats cost 3 × the price.',
    priceHintPerSeat: 'The amount charged for each seat a resident books.',
    perSeatFullBookingPreview: (seats: number, amount: string): string =>
        `A full booking (${seats} ${seats === 1 ? 'seat' : 'seats'}) costs ${amount}.`,
    // Mirrors the backend cap (PER_SEAT_MAX_TOTAL_CENTS in amenities/models/constants.py): Stripe
    // refuses a single charge above $999,999.99, so a per-seat price × capacity over it could never
    // actually be paid. Caught here so the admin learns before Save, not from a 400.
    perSeatTotalTooHigh: 'A full booking would cost more than $999,999.99. Lower the price per seat or the capacity.',
    priceInvalid: 'Enter a price of $0 or more, with at most 2 decimal places.',
    // Once "This amenity is chargeable" is on and the property CAN collect, $0 is no longer a valid
    // save — that's what the toggle exists to distinguish from a real charge (see chargeableLabel's
    // own doc comment). A LOCKED property is deliberately NOT held to this stricter rule (Manage/
    // index.tsx falls back to priceRule while locked) — lowering/zeroing an existing legacy price is
    // the remedy path and must never be blocked client-side.
    priceRequiredWhenChargeable: 'Enter a price of at least $0.01.',
    // The inline panel revealed when the toggle is locked and the admin either already has a legacy
    // price (chargeable defaulted on) or just tried to turn it on. One sentence per reason, in the
    // pricingGateMessage() voice (pricingGate.ts) — always naming the property (an admin managing
    // several sites needs to know which one to go fix) and always ending on the same payoff so the
    // five variants read as one family. Kept as plain functions (not JSX) so pricingGate.ts's pure
    // pricingGateMessage can be unit-tested with exact-string assertions, no rendering involved.
    pricingGateNotSetUp: (siteName: string): string =>
        `Payments aren't set up for ${siteName} yet. Set this up first, then you can make this amenity chargeable.`,
    pricingGateOnboardingIncomplete: (siteName: string): string =>
        `Stripe setup for ${siteName} isn't finished yet. Complete it, then you can make this amenity chargeable.`,
    pricingGateRevoked: (siteName: string): string =>
        `Stripe access for ${siteName} was revoked. Reconnect it, then you can make this amenity chargeable.`,
    pricingGateCompanyDisabled: (siteName: string): string =>
        `Amenity payments aren't turned on for your company yet, so ${siteName} can't collect. Turn that on, then you can make this amenity chargeable.`,
    pricingGateUnknown: (siteName: string): string =>
        `We couldn't confirm payments are set up for ${siteName}. Check its setup, then you can make this amenity chargeable.`,
    // Used only when the currently selected site's real name hasn't resolved yet (still loading) —
    // never left blank, since "Payments aren't set up for  yet." reads as broken.
    pricingGateFallbackSiteName: 'this property',
    // The panel's in-place remedy (design brief §3): PLATFORM collect-mode needs no Stripe
    // onboarding, no OAuth, no redirect — it creates the account-less row and the property becomes
    // chargeable immediately, so this button resolves in place rather than navigating away (unlike
    // the secondary link below, which is a deliberate hand-off to the fuller direct-payout setup).
    pricingGateCollectAction: 'Let Swiftlane collect payments',
    pricingGateDirectPayoutsLink: 'Set up direct payouts',
    pricingGateCollectSuccess: 'Swiftlane is now set up to collect payments for this property.',
    pricingGateCollectFailed: "Couldn't set up payments for this property.",
    // Fix round 1 (Finding 3): COMPANY_DISABLED is the one reason where NEITHER remedy in the panel
    // actually helps — the collect-mode write would succeed (it only checks the SITE, not the
    // company flag) and report success while changing nothing observable, which is worse than no
    // remedy at all; the direct-payout link is equally beside the point. Shown in place of both.
    pricingGateCompanyDisabledNote:
        "Only a Swiftlane super-admin can turn this on for your company — enrolling this property won't fix it on its own.",
    // Whole-branch review, FINDING A (half 2): shown INSTEAD of the "Let Swiftlane collect payments"
    // button whenever the locked reason is merely UNKNOWN. That button switches the property to
    // platform-collect, which on a property that already owns a working Stripe account MOVES WHERE
    // MONEY LANDS — off its own account, onto Swiftlane's, with manual remittances. UNKNOWN is
    // exactly the state in which we have NOT established that the property can't already collect, so
    // offering it there risks rerouting real money on a guess. Explain, and point at the payments
    // screen where the property's real status is visible, instead.
    pricingGateUnknownNote:
        "We couldn't confirm how this property collects, so we haven't offered to change it — that could move where its payments land. Open the payments screen to see its real setup.",
    // Shown inline on the Pricing card (not a dead/hidden field) when the company's cap_settings
    // lacks enable_amenity_payments — the fix for a real problem: an amenity can otherwise carry a
    // price on a building where nothing is ever actually charged.
    pricingPaymentsDisabledNotice: "Payments aren't enabled for this account, so residents won't be charged.",
    // Task 4 (2026-09-28), pricingGate.ts's `resolveStalePricingWarning`: the gate above stops a
    // NEW price from being set on a site that can't collect; it cannot stop a working site being
    // disconnected AFTERWARD, or catch a price set before the gate ever existed. This is the
    // permanent companion — a different statement from the gate's own "you can't turn this on yet"
    // message it sits beside (which already names the specific reason), so this one stays
    // reason-agnostic and states the consequence plainly instead of repeating the cause.
    stalePricingNotCharging: "This amenity is priced, but residents aren't being charged — the property can't currently collect payments.",
    // Overview tab vital (Detail/overviewHelpers.ts's buildVitals) — visible without opening the
    // editor at all, so unlike the note above it has no neighboring gate message to carry the WHY.
    // Short, distinct value strings per reason (pricingGate.ts's stalePricingVitalValue).
    notChargingVitalLabel: 'Not charging',
    stalePricingVitalNotSetUp: 'Payments never set up',
    stalePricingVitalOnboardingIncomplete: 'Stripe setup incomplete',
    stalePricingVitalRevoked: 'Stripe disconnected',
    stalePricingVitalCompanyDisabled: 'Company payments off',
    stalePricingVitalUnknown: 'Payments status unknown',
    // Tier money preview — renders the Cancellation policy tiers in real money once a price is set,
    // e.g. "48h+ before: free · 24–48h: $6.25 · under 24h: $12.50" (built by
    // cancellationTierMoneyPreviewText in helpers.ts, using the exact same first-matching-tier rule
    // as the backend's `_resolve_cancellation_charge`).
    pricingTierPreviewIntro: 'What a cancellation would cost at this price:',
    pricingTierPreviewIntroPerSeat: 'What a cancellation would cost, per seat booked:',
    pricingTierPreviewFree: 'free',
    // A single tier applies at ANY lead time (it's both the first tier a lead time can clear AND
    // the fallback "below every floor" tier — see resolveCancellationChargeCents), so it gets its
    // own label rather than the misleading "Xh+ before" (which would imply it stops applying below
    // its own floor, when it does not).
    pricingTierPreviewSingle: 'Any time before start',
    pricingTierPreviewFirst: (hours: number): string => `${hours}h+ before`,
    pricingTierPreviewRange: (lowHours: number, highHours: number): string => `${lowHours}–${highHours}h`,
    pricingTierPreviewLast: (hours: number): string => `under ${hours}h`,
    pricingTierPreviewLine: (label: string, amount: string): string => `${label}: ${amount}`,
    pricingTierPreviewSeparator: ' · ',
    // B4: dynamic, tier-based cancellation policy (time-before-start -> charge %) + optional hard
    // cancel deadline. Empty section = today's default behavior (always a full refund, always
    // cancellable) — sent to the API as `cancellation_policy: null`.
    cancellationPolicy: 'Cancellation policy',
    cancellationPolicyHint:
        'Charge a percentage of the price depending on how close to the start time a resident cancels. Leave this section empty for the default: always a full refund, cancellable any time.',
    cancellationTierHoursLabel: 'Hours before start',
    cancellationTierChargeLabel: 'Charge %',
    cancellationTierHoursPlaceholder: 'Hours',
    cancellationTierChargePlaceholder: '%',
    addCancellationTier: 'Add tier',
    removeCancellationTier: 'Remove',
    cancellationTierHoursInvalid: 'Enter a whole number of hours (0 or more).',
    cancellationTierChargeInvalid: 'Enter a charge between 0 and 100.',
    cancellationTierDuplicateHours: 'Each tier must have a different number of hours before start.',
    noCancelWithinHoursLabel: 'Block cancellation within (hours)',
    noCancelWithinHoursHint: 'Optional. Residents cannot cancel once this many hours (or fewer) remain before the start time.',
    // Review follow-up: a deadline set with zero tiers still saves fine (cancellation_policy is
    // sent as `null` — an empty section, deadline included), but is probably not what the admin
    // meant, so a light non-blocking nudge replaces the normal hint in that state.
    noCancelWithinHoursNoTiersHint: 'Add at least one tier to save this deadline — an empty section is not saved.',
    noCancelWithinHoursPlaceholder: 'No deadline',
    noCancelWithinHoursInvalid: 'Enter a whole number of hours (0 or more), or leave this blank.',
    // §6.3 admin reporting.
    dateRange: 'Date range',
    reportsRangeInvalid: 'Select a start and end date, with the end on or after the start.',
    reportsSelectRange: 'Select a date range above to view the report.',
    // P2-A (2026-08-27 report-unification polish round): "Usage" -> "How it gets used" (a question
    // heading, matching the all-amenity page's own style). The Demand/Money section headings/subs
    // and the "Reason turned away" column now reuse the all-amenity page's OWN texts
    // (reportsAllDemandTitle/reportsAllDemandSub/reportsAllMoneyTitle/reportsAllMoneySub/
    // reportsAllDenialsColReason below) rather than keeping a second, driftable copy of the same
    // words — `reportsDemandTitle`/`reportsMoneyTitle`/`reportsDenialType` used to live here.
    reportsUsageTitle: 'How it gets used',
    reportsUtilization: 'Utilization',
    // P3-G: "Ghost bookings" was engineering vocabulary for what the tooltip already defines as a
    // no-show (booked, but nobody showed — no door badge-in, no guest pass used).
    reportsNoShows: 'No-shows',
    reportsNoShowsTooltip: 'Confirmed bookings with no door activity — nobody badged in, and no guest pass was used.',
    reportsBookedCount: 'Booked',
    reportsHeatmapTitle: 'Bookings by day & hour',
    reportsDenialCount: 'Count',
    reportsDenialDiscounted: 'Duplicate joins discounted',
    reportsNoDenials: 'No denials in this range.',
    reportsPromoted: 'Promoted',
    reportsWaitlisted: 'Waitlisted',
    reportsPromotionRate: 'Promotion rate',
    reportsWaitlistDepthTitle: 'Waitlist depth (upcoming)',
    reportsNoWaitlistDepth: 'No waitlist activity in range.',
    reportsDepth: 'Depth',
    reportsLeadTimeTitle: 'Cancellation lead time',
    reportsLeadTimeBucket: 'Before start',
    reportsLeadTimeCount: 'Cancellations',
    reportsDoorDataCapped:
        'No-show counts only reflect the last 30 days of door-access history — bookings older than that are excluded rather than mis-counted.',
    exportUsageCsv: 'Export usage CSV',
    exportDemandCsv: 'Export demand CSV',
    // F6: the CSV endpoints are GETs whose body IS the raw CSV text; useRequestApi's success toast
    // (getSuccessMsg returns a string body verbatim) dumped that CSV into a toast. Call sites now
    // suppress that auto-toast (popupSuccessMsg:false) and show this clean confirmation instead.
    csvDownloaded: (filename: string): string => `Downloaded ${filename}.csv`,
    // Admin attendees view (per-booking Attendees affordance on the Bookings page): who's coming
    // — booker + guest passes + co-resident party members.
    attendees: 'Attendees',
    attendeeBooker: 'Booker',
    attendeeGuests: 'Guests',
    attendeeCoResidents: 'Co-residents',
    attendeeGuestFallbackName: 'Guest',
    attendeeCoResidentFallbackName: 'Co-resident',
    noGuests: 'No guests.',
    noCoResidents: 'No co-residents.',
    // Headcount badge on the toggle: below the seat cap shows "N of S seats" (cap awareness);
    // at/over the cap shows a plain attending count.
    attendeesCount: (n: number): string => `${n} attending`,
    attendeesOfSeats: (n: number, seats: number): string => `${n} of ${seats} seats`,
    // Console redesign — Settings tab field help. Copy explains CONSEQUENCE, not the label
    // (fieldHelp.test.ts pins non-empty + "help !== label" for every key here) — an admin should
    // learn what changes for residents/doors/money, not just get the label re-typed at them. Keys
    // match the Form.Item `name` (or, for section-level help — `windows` — the Card `title`) each
    // string sits beside in Manage/index.tsx.
    //
    // QA item 8 (Atul, 31-Aug): "Instead of i icons, add help text directly below the field wherever
    // possible - We are following this from long time." These strings used to be TOOLTIP copy behind
    // a ⓘ (the deleted Manage/FieldHelp component); they are unchanged as copy, but are now rendered
    // as always-visible text below their field (Form.Item `extra`) or, for `windows`, under its Card
    // title. Because they are always on screen now, a string that merely restates something already
    // written next to the field would read as a stacked duplicate — which is why
    // `accessMode` was dropped here in that same pass (its one-sentence summary of the three access
    // modes sat directly above the three per-option descriptions that say the same thing at greater
    // length), joining graceMinutes/maxGuestPins/waitlistMaxPerSlot (P3-D) and
    // cancellationPolicyTiers/noCancelWithinHours/photos (F15a), which had already been dropped for
    // exactly the same reason while the ⓘ still existed.
    fieldHelp: {
        name: 'Shown to residents in the booking list and on confirmation emails — pick something recognizable, e.g. "Rooftop Pool" rather than "Amenity 3".',
        // Review fix: fact-checked against the backend — it gates on SEATS, not booking count
        // (booking_service.py:236,252 — `seats_taken(...) + seats > amenity.capacity`), and
        // residents pick a party size 1..capacity (UserAmenities/Book/index.tsx:197), so one
        // 3-seat booking alone fills a capacity-3 slot. Exact reviewer-mandated copy — see
        // fieldHelp.test.ts's EXACT_HELP_TEXT.
        capacity:
            'The number of spots a time slot has, shared across however many bookings claim them — a resident can book more than one spot at once (e.g. a party of 3), so Capacity 3 could be one booking of 3 or three bookings of 1. The slot only shows as full once every spot is claimed.',
        slotMinutes:
            'How long each bookable block is. Residents pick from slots this length, laid back-to-back across the hours you set below.',
        bufferMinutes: "Cleanup gap between bookings — the next slot can't start inside it, even if it would otherwise fit.",
        leadTimeMinutes:
            "How soon before the start a resident must book. 60 minutes means nothing in the next hour is offered, even if it's open.",
        maxAdvanceDays:
            'How far into the future a booking can be made. 30 means nothing past 30 days out is ever offered, no matter how open the calendar is.',
        visibilityMode:
            'Who can even see this amenity to book it. Restricting it hides it entirely from everyone outside the selected groups — not just the booking button.',
        // QA item 8: `accessMode`'s entry was REMOVED here. Its copy ("Decides what happens at the
        // door during a booking — from no change at all (Shared), to only confirmed bookers getting
        // in (Booking only), to the doors admitting nobody but the booker's party, staff and admins
        // (Exclusive)") was a one-sentence précis of accessModeSharedDesc/accessModeBookingOnlyDesc/
        // accessModeExclusiveDesc, which render as always-visible copy beside each radio option. It
        // was tolerable tucked inside a ⓘ; as a help line printed directly under those same three
        // descriptions it would be a visible duplicate. The per-option descriptions ARE this field's
        // help.
        staffGroupId:
            'Members of this group can still badge in during an Exclusive booking, when everyone else would be refused — use it for staff, engineers or cleaners who need access regardless of who booked.',
        // P3-D (polish round): the graceMinutes/maxGuestPins/waitlistMaxPerSlot ⓘ tooltip ENTRIES
        // were REMOVED from this map (and their ⓘ icons removed from Manage/index.tsx) — each of
        // those 3 fields already carries its own always-visible inline `extra` hint
        // (graceMinutesHint/maxGuestPinsHint/waitlistMaxPerSlotHint above, UNCHANGED, still the
        // field's help) saying the same thing, so the field carried the identical explanation
        // twice. Nothing else in the wizard is duplicated this way, so no other entry here changed
        // — see fieldHelp.test.ts's updated field list.
        requireGuestNames:
            'When on, a booker must type a real name for every guest pass instead of leaving it blank — useful for front-desk or security logs that need to match a name to a PIN.',
        waitlistEnabled:
            "Lets residents queue for a full slot instead of being turned away — they're notified and get first refusal automatically if a spot opens up.",
        // D1/D2/D3. Three facts an admin needs before flipping this, in the order they bite: what a
        // resident's tap on Book now does, what happens to the seat while the request waits, and
        // what happens to their money. The last one is the whole design — approve first, pay after
        // — so a rejected or expired request never has anything to refund.
        requiresApproval:
            'Bookings become requests you approve or decline, instead of confirming on the spot. The seat is held while a request waits, so approving always gets the resident the slot they asked for. Nothing is charged until you approve — on a paid amenity they only get the payment link once you say yes.',
        // F15a (round-2 critique, polish round 2): cancellationPolicyTiers/noCancelWithinHours/
        // photos ⓘ ENTRIES were REMOVED from this map (and their ⓘ icons removed from
        // Manage/index.tsx) — each of those 3 sections/fields already carries its own
        // always-visible inline hint (cancellationPolicyHint, noCancelWithinHoursHint/
        // noCancelWithinHoursNoTiersHint, photosHint — all unchanged, still the section/field's
        // help) saying the same thing, so the ⓘ was a duplicate. Same reasoning P3-D already
        // applied to graceMinutes/maxGuestPins/waitlistMaxPerSlot above — see fieldHelp.test.ts's
        // updated field list.
        windows:
            'Defines when this amenity can be booked at all. Outside these windows there is nothing to pick, no matter how the lead time or advance-day settings are configured.',
        // Review fix: newly added. Fact-checked — doors.py:42,103 writes one grant/exclusive-
        // window PER MAPPING ROW, so zero mapped doors on a Booking-only/Exclusive amenity
        // restricts nothing at any door. Exact reviewer-mandated copy — see fieldHelp.test.ts's
        // EXACT_HELP_TEXT.
        accessPointIds:
            "Which physical doors this amenity's access rules apply to. Leave it empty under Booking-only or Exclusive and there's nothing to actually restrict — no door changes at all.",
    },
    // List page redesign — search, status filter, Today column, humanized hours.
    searchPlaceholder: 'Search amenities…',
    searchAriaLabel: 'Search amenities',
    // Copy nit (polish round): was 'Status: all', which repeats the field prefix the Select's own
    // aria-label/label already carries.
    statusFilterAll: 'All',
    statusFilterAriaLabel: 'Status filter',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    // P1-A(b): shown instead of the raw ACTIVE status whenever `isAmenityBookable` says the
    // amenity can't actually be booked (no hours, or Booking-only/Exclusive with no doors mapped)
    // — status should reflect bookability, not just the DB flag. INACTIVE is left alone; that's
    // the admin's own deliberate off switch, a different question from bookability.
    statusNeedsSetup: 'Needs setup',
    today: 'Today',
    todayUnavailable: '—',
    // P1-A(a): shown in the Today cell (no bar) for an amenity `isAmenityBookable` says can't be
    // booked, instead of a misleadingly-calm "0/0 slots" empty bar.
    todayNotBookable: 'Not bookable',
    // P1-B(c): the activity filter chip row, beside the existing Status <Select>.
    activityFilterAriaLabel: 'Activity filter',
    activityFilterAll: 'All',
    activityFilterBusyToday: 'Busy today',
    activityFilterNeedsAttention: 'Needs attention',
    // Consolidation pass (2026-08-28): the zero-result search/filter empty state (listHelpers.ts's
    // listEmptyState) used to render antd's bare default on both the desktop Table and the mobile
    // card list — these pair with its computed title on the one action that resolves it.
    clearSearch: 'Clear search',
    clearFilters: 'Clear filters',
    waitlistedBadge: (n: number): string => `⏳ ${n} waitlisted`,
    openAmenity: (name: string): string => `Open ${name}`,
    // Mobile card-list redesign (fix round) — compact row-2 meta line, e.g. "Cap 3 · Mon–Sun 9:00 – 17:00".
    capacityAbbrev: (capacity: number): string => `Cap ${capacity}`,
    // Bookings-tab redesign (2026-08-27): Who-column chip summary + week strip/slot table.
    attendeeChipGuests: (n: number): string => `+${n} ${n === 1 ? 'guest' : 'guests'}`,
    bookingsWeekPrev: 'Previous week',
    bookingsWeekNext: 'Next week',
    bookingsToday: 'today',
    bookingsHasBlocks: 'has blocks',
    // Consolidation pass (2026-08-28): the week strip's day-aggregate fill figure counts an admin
    // block's claimed seats the same as a resident booking (weekStripHelpers.ts's weekDayFillLabel
    // has no per-slot booking breakdown at the day level to separate the two, unlike the per-slot
    // table below it, which does). Now that the backend excludes blocks from resident booking
    // counts everywhere else in this console, a bare "N/M" fraction (implicitly read as "booked")
    // would overstate real demand — relabeled "in use" (which is honestly true either way) rather
    // than fetching more data just for this summary strip. Mirrors reportsBusyIncludesBlocks'
    // wording above for the same underlying distinction.
    bookingsWeekStripFillLabel: (fraction: string): string => `${fraction} in use`,
    bookingsWeekStripFillTooltip: 'Includes seats claimed by admin blocks, not just resident bookings.',
    bookingsTime: 'Time',
    bookingsBooked: 'Booked',
    bookingsWho: 'Who',
    bookingsWaitlist: 'Waitlist',
    bookingsStatus: 'Status',
    bookingsActions: 'Actions',
    bookingsStatusOpen: 'Open',
    bookingsStatusFull: 'Full',
    bookingsStatusBlocked: 'Blocked',
    bookingsStatusPast: 'Past',
    bookingsStatusClosed: 'Closed',
    bookingsUnblock: 'Unblock',
    bookingsUnblockConfirm: 'Unblock this slot? Residents will be able to book it again.',
    // Bookings-tab polish round (2026-08-28), from a binding design critique:
    // P1-A a blocked slot's WHO cell shows the admin's block reason instead of repeating the word
    // "Blocked" a third time (STATUS already says it) — falls back to a neutral phrase when the
    // backend hasn't sent block_reason yet (or it was left blank).
    blockReasonUnavailable: 'No reason given',
    // P1-B WHO-column real-name fallback chain (attendeesHelpers.ts's bookerDisplayName) — the
    // last-resort placeholder when booker_name, unit_name AND email are all absent. Never a bare
    // dash.
    residentNameUnavailable: 'Resident (name unavailable)',
    // P1-B secondary "· Unit 204" line under the booker's real name.
    whoUnitLabel: (unit: string): string => `Unit ${unit}`,
    // P1-B combined co-resident count chip, mirroring attendeeChipGuests's own "+N" convention —
    // replaces one named chip per co-resident so the WHO cell reads as name + two compact counts,
    // not a wall of chips.
    attendeeChipCoResidents: (n: number): string => `+${n} ${n === 1 ? 'co-resident' : 'co-residents'}`,
    // P2-C compact per-row action labels (the per-slot Block/Book-on-behalf buttons specifically —
    // deliberately distinct from blockSlot/bookOnBehalf, which stay as the fuller modal titles).
    bookingsActionBlock: 'Block',
    bookingsActionBook: 'Book',
    bookingsDayClosedTitle: 'Closed',
    bookingsDayClosedDesc: 'This amenity has no operating hours configured for this day.',
    // Consolidation pass (2026-08-28): the closed-day empty state named the cause but dead-ended
    // there — this pairs it with the one action that resolves it, mirroring the closed-day empty
    // state's own quality bar (explain + offer the fix), pointed at this amenity's own Settings tab.
    bookingsDayClosedSetHours: 'Set hours →',
    bookingsLoadErrorTitle: "Couldn't load bookings for this day",
    bookingsRetry: 'Retry',
    // F10: "six of eight rows on a typical day render 0/3 · – · – · Block Book" — a toggle to hide
    // slots with no booking, waitlist entry, or block (slotTableHelpers.ts's slotRowHasActivity).
    // Default OFF (nothing hidden from an admin who hasn't asked); the label itself, plus the
    // switch's own on/off state, is what makes the filter's state obvious.
    bookingsOnlyActivityToggle: 'Only show slots with activity',
    // Shown instead of a silently empty table when the filter hides every slot that day.
    bookingsOnlyActivityEmpty: 'No booked, waitlisted, or blocked slots — every slot today is open.',
    bookingsOnlyActivityShowAll: 'Show all slots',

    // ===== stuck payments (admin payment controls, BE PR #3086) =====
    // The admin bookings payload now flags bookings whose money and whose booking disagree. The
    // flag is set by the backend and is deliberately co-extensive with "reconciling will actually
    // do something", so every string here can promise an action without hedging.
    paymentAttentionTag: 'Payment issue',
    // More than one stuck booking in the same slot — a real case at capacity > 1.
    paymentAttentionTagCount: (n: number): string => `Payment issue (${n})`,
    // The two reasons, told apart because they are genuinely different stories: the first is money
    // Stripe already took for a booking that never happened; the second is a booking nobody ever
    // asked Stripe about.
    paymentAttentionCaptured: 'Paid, but the booking never completed.',
    paymentAttentionUnresolved: 'The payment was never resolved.',
    // A reason this build has not been taught. Says the true, useful part rather than nothing.
    paymentAttentionGeneric: 'This booking’s payment needs attention.',
    bookingsActionFixPayment: 'Fix payment',
    fixPaymentConfirm: 'Check this booking’s payment against Stripe and settle it?',
    // What the converge actually did, in plain words, from `action_taken` — an operator should
    // never have to read a status code.
    reconcileRecovered: 'Booking recovered — the payment landed and the booking is confirmed.',
    reconcileRefunded: 'Refunded in full — the booking couldn’t be honoured.',
    reconcileClosedOut: 'Payment never completed. The seat has been offered to the waitlist.',
    reconcileLeftInFlight: 'Payment still in progress — try again in a few minutes.',
    // Nothing changed, on purpose. This is the one outcome that needs a person, so it must not
    // read like a success.
    reconcileMismatched: 'Needs manual review — Stripe and our records disagree on the amount.',
    // "There WAS money to decide about, and something else already decided it" — NOT the same fact
    // as reconcileNoPayment below, which is why the backend gave them different HTTP codes.
    reconcileSkipped: 'Already up to date — nothing needed changing.',
    reconcileSkippedPaymentsDisabled: 'Payments are turned off for this account, so nothing was charged or refunded.',
    // The 409. A normal "nothing to do": this booking has no Stripe payment at all.
    reconcileNoPayment: 'This booking has no Stripe payment to reconcile.',
    // Fallback for an outcome a later backend adds. Truthful without claiming a specific result.
    reconcileDone: 'Payment checked.',
    // The 502. Session was rolled back and the operation is idempotent, so "try again" is real
    // advice rather than a platitude.
    reconcileFailed: 'Couldn’t check this payment right now. It’s safe to try again.',

    // L6 — all-amenity reports (console redesign screen 3), GET /admin/amenity-reports.
    reportsAllTitle: 'Amenity reports',
    reportsAllPresetLast7: 'Last 7 days',
    reportsAllPresetLast30: 'Last 30 days',
    reportsAllPresetQuarter: 'This quarter',
    reportsAllLoadErrorTitle: "Couldn't load reports",
    reportsAllRetry: 'Retry',
    reportsAllEmptyTitle: 'Nothing to report yet',
    reportsAllEmptyDesc: 'Add an amenity to start seeing usage, demand and revenue reports here.',
    reportsAllKpiBookings: 'Bookings',
    reportsAllKpiUtilization: 'Utilization',
    reportsAllKpiUtilizationSub: 'of all slot-seats',
    reportsAllKpiWaitlisted: 'Waitlisted',
    reportsAllKpiPromotedSub: (n: number): string => `${n} promoted`,
    reportsAllKpiTurnedAway: 'Turned away',
    reportsAllKpiTurnedAwaySub: 'denied bookings',
    reportsAllKpiNetRevenue: 'Net revenue',
    reportsAllKpiNetRevenueSub: (collected: string, refunded: string): string => `${collected} collected − ${refunded} refunded`,
    reportsAllRankedTitle: 'Which amenities get used',
    reportsAllRankedSub: "Ranked by utilization. Click any row to open that amenity's own report.",
    // P3-G: the zero-utilization long tail (every amenity with no bookings in range) is collapsed
    // by default — those rows are noise, not signal, and push the amenities worth acting on further
    // down the page.
    reportsAllZeroBookingCollapsed: (n: number): string => `${n} amenit${n === 1 ? 'y' : 'ies'} with no bookings in this range`,
    reportsAllShowMore: 'show',
    reportsAllHideZeroBooking: 'hide',
    reportsAllColAmenity: 'Amenity',
    reportsAllColUtilization: 'Utilization',
    reportsAllColBookings: 'Bookings',
    reportsAllColAttendees: 'Attendees',
    reportsAllColTrend: 'Trend',
    // F6: "▲ 18%" alone states no denominator — an admin can't act on a percentage without knowing
    // what it's measured against. Used both as the Bookings KPI card's subtitle (appended to its
    // trend figure) and as the ranked table's own Trend-column subhead, so the SAME number reads
    // consistently in both places. `n` is the actual selected range's own day count (never
    // hardcoded — the backend compares to a period the same LENGTH as whatever's selected, not
    // always literally 30 days; see trendComparisonDays in reportsHelpers.ts).
    reportsAllTrendComparisonSub: (n: number): string => `vs previous ${n} day${n === 1 ? '' : 's'}`,
    reportsAllColWaitlist: 'Waitlist',
    reportsAllColNet: 'Net',
    // F6: an unpriced amenity's Net cell used to render the same bare "—" as a genuinely-missing
    // figure — reading as "no data" when it actually means "this amenity isn't priced". The ranked
    // table stays (dropping it loses the at-a-glance cross-amenity revenue comparison it exists
    // for), just with an honest label for this one cell.
    reportsAllColNetNotPriced: 'Not priced',
    reportsAllOpenAmenityReport: (name: string): string => `Open ${name}'s report`,
    reportsAllDemandTitle: 'Unmet demand',
    reportsAllDemandSub: "Where residents wanted in and couldn't.",
    reportsAllDenialsColReason: 'Reason turned away',
    reportsAllDenialsColCount: 'Count',
    reportsAllDenialsColWorst: 'Worst amenity',
    // Raw backend enum code -> human label (or a stripped/title-cased fallback for an unrecognized
    // code). Shared across BOTH report screens — the all-amenity "Reason turned away" column + its
    // CSV export, AND the per-amenity Reports tab's "Denial type" column — so the raw code (e.g.
    // BOOKING_DENIED_FULL) never leaks into any of them. Fix round: the per-amenity tab used to
    // render `record.type` verbatim while this all-amenity page already humanized it, so the same
    // denial showed a friendly label on one report screen and the raw backend enum on the other.
    // One shared function now backs every surface, rather than a second copy of this lookup.
    denialReasonLabel: (reason: string): string => DENIAL_REASON_LABELS[reason] ?? titleCaseWords(reason.replace(/^BOOKING_DENIED_/, '')),
    reportsAllNoDenials: 'No denials in this range.',
    // P2-D: unmet-demand tables (both report screens) are sorted by count descending; the reason
    // types that had zero denials in range are collapsed behind this one summary line rather than
    // printed as N more all-zero rows.
    reportsDenialsOthersCollapsed: (n: number): string => `${n} other reason${n === 1 ? '' : 's'} — none in this range`,
    reportsAllWaitlistTitle: 'Waitlist pressure',
    reportsAllWaitlistSub: 'Queued now, and how often the queue actually converts.',
    reportsAllWaitlistColInLine: 'In line',
    reportsAllWaitlistColPromoted: 'Promoted',
    reportsAllWaitlistColConversion: 'Conversion',
    reportsAllNoWaitlistPressure: 'No waitlist activity in range.',
    reportsAllBusyTitle: 'When the building is busy',
    reportsAllBusySub: 'All amenities combined — for staffing, cleaning and hours decisions.',
    reportsAllBusyLegendQuieter: 'Quieter',
    reportsAllBusyLegendBusier: 'Busier',
    // Polish round (2026-08-27): retired `reportsAllBusyCellTitle` (used to live here) — the per-cell
    // tooltip is now built by the shared Heatmap component's own `cellTitle` helper
    // (Reports/shared/heatmap.ts). It names the cell's actual hour SPAN (the transposed grid bins 2
    // hours per row) rather than a single instant, so it lives with that formatting logic instead of
    // as a texts-module template; coverage moved to shared/Heatmap.test.tsx.
    // One-line prose summary above the grid — the sentence an admin actually needs ("Busiest:
    // Thursdays 3–4 PM, then Wednesdays 6–7 AM.") without reading a single cell. `second` is `null`
    // when there is only one busy slot in range (no "then …" clause).
    reportsBusySummary: (first: string, second: string | null): string =>
        second ? `Busiest: ${first}, then ${second}.` : `Busiest: ${first}.`,
    reportsBusyNoData: 'No bookings in this range yet.',
    // The heatmap counts every OCCUPYING slot — admin blocks included, same as the single-amenity
    // lens (`reporting.heatmap()`) already documents — while the ranked table's own bookings/
    // attendees figures count resident bookings only. Without this line the two numbers on the same
    // screen can look like they disagree; they don't, they're answering different questions.
    reportsBusyIncludesBlocks: 'Includes maintenance blocks',
    reportsAllMoneyTitle: 'Money',
    reportsAllMoneySub: 'Priced amenities only. Retained = cancellation fees kept under the policy.',
    reportsAllMoneyColCollected: 'Collected',
    reportsAllMoneyColRefunded: 'Refunded',
    reportsAllMoneyColRetained: 'Retained fees',
    reportsAllMoneyColNet: 'Net',
    reportsAllMoneyTotal: 'Building total',
    reportsAllExportCsv: 'Export CSV',
    reportsAllCsvFilename: (start: string, end: string): string => `amenity-reports-${start}-to-${end}`,
    // P3-G: this used to claim "every section exports to CSV" — false, there is exactly one Export
    // CSV button at the top of the page (it does bundle every section into that one file, which is
    // the part worth keeping in the copy).
    reportsAllFooterNote:
        "Export CSV (above) downloads every section on this page as one file. Rows link into each amenity's own report for the drill-down.",
    // ===================== Approval flow (backend PR #3088) =====================
    // Product spine, repeated in every string below so the copy tells ONE story: approve first,
    // pay after. A request takes no money, so a rejected or expired one never had any to refund —
    // that is the whole reason the flow has no refund path, and admins should be told it plainly
    // rather than left to worry that declining someone costs the building a Stripe fee.
    approvals: 'Approvals',
    // Shown on the queue's own header, under the title.
    approvalsSubtitle: 'Requests waiting on you. Nothing has been charged — on a paid amenity the resident only pays after you approve.',
    // The two halves of the queue. REQUESTED is work (a decision you owe); APPROVED is a chase (you
    // decided, the resident owes money). Deliberately labelled by whose move it is.
    approvalsQueueRequested: 'Waiting on you',
    approvalsQueueApproved: 'Waiting on payment',
    approvalsQueueAriaLabel: 'Approval queue',
    approvalsResident: 'Resident',
    approvalsAmenity: 'Amenity',
    approvalsSlot: 'Slot',
    approvalsSeats: 'Seats',
    // The card has no column header to inherit meaning from, so seats label themselves there.
    approvalsSeatsCount: (n: number): string => (n === 1 ? '1 seat' : `${n} seats`),
    approvalsRequestedAt: 'Requested',
    approvalsPayment: 'Payment',
    approvalsPayBy: 'Pay by',
    approvalsApprove: 'Approve',
    approvalsReject: 'Reject',
    // `title` on the header badge — the count alone is a glyph; this says what it counts.
    approvalsBadgeTitle: (n: number): string => `${n} ${n === 1 ? 'request' : 'requests'} waiting for approval`,
    noPendingApprovals: 'Nothing waiting. Requests land here as residents book an amenity that requires approval.',
    noApprovedAwaitingPayment: 'Nobody owes money. Approved bookings sit here until the resident pays for them.',
    // What approving will DO to money — read off the queue row's own frozen snapshot.
    approvalPaymentFree: 'Free — approving confirms it',
    approvalPaymentAfterApproval: (amount: string): string => `${amount} — resident pays after you approve`,
    // D3, stated as the RULE rather than a timestamp: the real deadline is stamped by the backend at
    // approve time (`payment_due_by = min(now + 24h, slot start)`), and the console has no endpoint
    // that exposes the window setting, so quoting a computed instant here would be a client-side
    // guess. The concrete deadline is shown afterwards, from the server's own response.
    approveConfirmPaid: (name: string, amount: string): string =>
        `Approve ${name}'s request? This charges nobody. It lets them pay the ${amount} themselves, and they get 24 hours to do it — or until the slot starts, if that comes first. Miss it and the request lapses and the seat is freed.`,
    approveConfirmFree: (name: string): string =>
        `Approve ${name}'s request? This amenity is free, so the booking is confirmed straight away and door access opens for the slot. Nothing is charged.`,
    approveConfirmOk: 'Approve request',
    rejectRequestTitle: 'Reject request',
    rejectRequestBody: (name: string): string =>
        `${name} will be told their request was declined, and the seat goes back on the calendar. No money was ever taken, so there is nothing to refund.`,
    rejectReasonLabel: 'Reason (optional)',
    rejectReasonPlaceholder: 'Shown to the resident, e.g. Reserved for building maintenance',
    // Mirrors the backend's own 256-character bound so an over-long reason is caught here rather
    // than coming back as a 400 after the admin has already written it.
    rejectReasonTooLong: 'Reason must be 256 characters or fewer.',
    rejectConfirm: 'Reject request',
    approvalApprovedFree: (name: string): string => `Approved — ${name}'s booking is confirmed.`,
    approvalApprovedPaid: (name: string, due: string): string => `Approved — ${name} can pay now, and has until ${due}.`,
    approvalApprovedPaidNoDue: (name: string): string => `Approved — ${name} can pay now.`,
    approvalRejected: (name: string): string => `Rejected — ${name}'s seat is back on the calendar.`,
    // The three conflicts the contract defines, written as information rather than failure. Each
    // one means the admin's page was a moment behind reality, not that they did something wrong.
    approvalAlreadyDecided: 'That request was already decided. The queue below is up to date.',
    approvalSlotFull: 'That seat has already gone — another approval took the last one. Free a seat in the slot, or reject this request.',
    approvalUserNotEligible:
        "This resident can't book this amenity any more — they may have moved out, been deactivated, or had their unit suspended. Reject the request to free the seat.",
    approvalRequestGone: 'That request is no longer here — it was decided or withdrawn while this page was open.',
    // ===================== Approval states on the Bookings tab =====================
    // A request is NOT a confirmed booking, and an approved-but-unpaid one is not either. Both hold
    // a seat (D2), so the row's seat count already includes them; these labels are what stop the
    // WHO column reading as "someone is coming".
    bookingsStatusRequested: 'Awaiting approval',
    bookingsStatusApproved: 'Awaiting payment',
    bookingsApprovalTagRequested: (n: number): string => (n === 1 ? '1 awaiting approval' : `${n} awaiting approval`),
    bookingsApprovalTagApproved: (n: number): string => (n === 1 ? '1 awaiting payment' : `${n} awaiting payment`),
    bookingsReviewRequests: 'Review requests',
    // ===================== Terms & conditions + Insurance (TECHSPEC_AMENITY_TERMS_AND_INSURANCE.md) =====================
    // Looked up wherever a booking/queue row needs to render its raw compliance_state as a word —
    // see INSURANCE_STATE_LABELS above.
    insuranceStateLabel: (state: string): string => INSURANCE_STATE_LABELS[state] ?? titleCaseWords(state),
    // ----- Wizard: Manage/index.tsx's new Terms & conditions / Insurance cards -----
    termsCardTitle: 'Terms & conditions',
    termsBodyLabel: 'Terms shown to residents',
    termsBodyHint:
        'Shown to residents in the app before they book. Not enforced by Swiftlane — each app decides whether to require a tick, and only sees that the version changed.',
    // Fix round 1 (REVIEW_D_RESULT.md M4): the backend caps this at 20,000 chars
    // (`amenities_admin.py`); mirrors NAME_MAX_LENGTH's own "field message" pattern.
    termsTooLong: 'Terms must be 20,000 characters or fewer.',
    // "v3 · updated Sep 6, 2026, 10:00 AM" — the wizard's own provenance line under the textarea,
    // shown once the property has ever saved content (terms_version starts at 0 server-side).
    termsVersionLabel: (version: number, updatedAt: string): string => `v${version} · updated ${updatedAt}`,
    insuranceCardTitle: 'Insurance',
    insuranceRequiredLabel: 'Require proof of insurance',
    insuranceRequiredHint:
        'A resident must upload a certificate of insurance and have it approved before they get door access for this amenity. The slot itself still holds — money is never the gate.',
    // D9's rollout caveat, positioned directly under the toggle per the design brief — an admin who
    // turns this on for a building running an older app version would otherwise get a stream of
    // "why can't residents upload anything" tickets with no clue this is why.
    // AUTOCANCEL_SPEC.md C1 — the auto-cancel toggle. Always visible next to the require switch
    // (disabled-with-a-why rather than hidden, so an admin can discover it exists before flipping
    // insurance on); label and help text are the spec's own words, help rendered BELOW the field
    // (Atul's standing request: help text under fields, never an ⓘ icon).
    insuranceAutoCancelLabel: "Automatically cancel and refund if the certificate isn't approved by the deadline",
    insuranceAutoCancelHint:
        'The resident is reminded 7, 3 and 1 days before the deadline. If nothing is approved by then, the booking is cancelled and refunded in full.',
    // The disabled toggle's hover tooltip — every disabled control explains why on hover.
    insuranceAutoCancelDisabledReason:
        'Turn on "Require proof of insurance" first — auto-cancel only applies when a certificate is required.',
    insuranceRolloutWarning:
        'Residents can only upload from an app version that supports insurance — confirm with the app team before enabling.',
    insuranceInstructionsLabel: 'Instructions for residents',
    insuranceInstructionsHint: 'Shown to residents verbatim on the upload screen — what the certificate needs to show.',
    insuranceMinLiabilityLabel: 'Minimum liability coverage (USD)',
    insuranceMinLiabilityHint: 'Leave blank for no minimum. The AI check flags a certificate whose general-liability limit falls short.',
    insuranceMinLiabilityInvalid: 'Enter $0 or more, with at most 2 decimal places, or leave blank for no minimum.',
    insuranceAdditionalInsuredLabel: 'Additional insured name',
    insuranceAdditionalInsuredHint: 'Who the certificate must name, e.g. the HOA or property entity. Leave blank to skip this check.',
    // Fix round 1 (REVIEW_D_RESULT.md M4): the backend caps this at 256 chars.
    insuranceAdditionalInsuredNameTooLong: 'Additional insured name must be 256 characters or fewer.',
    // AUTOCANCEL_SPEC.md C2 — the due-window field now reads in DAYS (the customer's own language:
    // "COI due 7 days before day of usage"); storage/wire stays hours, see helpers.ts's
    // dueDaysToHours/dueHoursToDays. The older *DueHours* keys below stay for the hours-based
    // validators helpers.ts still exports.
    insuranceDueDaysLabel: 'Due (days before the slot)',
    insuranceDueDaysHint: 'How long before the slot the approved certificate is due.',
    insuranceDueDaysPlaceholder: 'Defaults to 7 days',
    insuranceDueDaysInvalid:
        'Enter between 1 hour and 30 days, in whole hours (e.g. 1.5 days = 36 hours), or leave blank to use the default (7 days).',
    insuranceDueHoursLabel: 'Due (hours before the slot)',
    insuranceDueHoursHint: 'How long before the booking the certificate must be approved by. Between 1 and 720 hours.',
    insuranceDueHoursPlaceholder: 'Defaults to 48',
    // Fix round 1 (REVIEW_D_RESULT.md M4): the backend requires an integer in [1, 720] when a
    // value is given (blank still means "use the default", isValidDueHours).
    insuranceDueHoursInvalid: 'Enter a whole number of hours between 1 and 720, or leave blank to use the default (48).',
    // ----- Approvals page: the new "Insurance" tab (queue + detail modal + 3 decisions) -----
    approvalsTabRequests: 'Requests',
    insuranceTab: 'Insurance',
    insuranceQueueSubtitle: 'Certificates of insurance waiting on your review. The AI check is advisory — the decision is yours.',
    insuranceQueueStateAriaLabel: 'Insurance queue state',
    insuranceQueueStateAwaitingReview: INSURANCE_STATE_LABELS.AWAITING_REVIEW,
    insuranceQueueStateChangesRequested: INSURANCE_STATE_LABELS.CHANGES_REQUESTED,
    insuranceQueueStateAwaitingDocument: INSURANCE_STATE_LABELS.AWAITING_DOCUMENT,
    insuranceColResident: 'Resident',
    insuranceColUnit: 'Unit',
    insuranceColAmenity: 'Amenity',
    insuranceColSlot: 'Slot',
    insuranceColDue: 'Due',
    insuranceColAi: 'AI check',
    // AI badge — advisory triage only (D6): the copy never says "approved"/"rejected", only what the
    // model extracted and whether code-side checks against the amenity's configured expectations
    // found anything.
    insuranceAiLooksValid: 'Looks valid',
    insuranceAiIssuesCount: (n: number): string => (n === 1 ? '1 issue' : `${n} issues`),
    insuranceAiUnreadable: 'Unreadable',
    insuranceAiNotChecked: 'Not checked',
    // FAILED or SKIPPED (no API key configured, or the check errored/timed out) — same badge either
    // way: there is deliberately no verdict to show, and the admin should decide from the document
    // itself rather than infer anything from an absent AI opinion.
    insuranceAiUnavailable: 'Unavailable',
    // Fix round 1 (REVIEW_D_RESULT.md M1): CONFIRMED — the backend sends `ai: null` for every row
    // with no live document yet (the whole AWAITING_DOCUMENT state). Distinct from "Not checked"
    // (PENDING — a document exists, the AI just hasn't run) and "Unavailable" (a document exists,
    // the check failed/was skipped) — this is the one badge that means there is nothing to check.
    insuranceAiNoDocument: 'No document',
    insuranceQueueEmpty: 'Nothing waiting. Reviews land here once a resident uploads a certificate.',
    insuranceApprove: 'Approve',
    insuranceRequestChanges: 'Request changes',
    insuranceDeny: 'Deny',
    insuranceRerunAi: 'Re-run AI',
    insuranceApproveConfirmTitle: (name: string): string => `Approve ${name}'s certificate?`,
    insuranceApproveConfirmBody: "This grants door access for the booking (if it's confirmed) and notifies the resident.",
    // Distinct copy from the row/card action button's own "Approve" (the Popconfirm's trigger stays
    // on screen behind the confirm popup — same ambiguous-double-button reason
    // insuranceRequestChangesConfirm's own comment documents).
    insuranceApproveConfirmOk: 'Yes, approve',
    insuranceRequestChangesTitle: 'Request changes',
    insuranceRequestChangesBody: (name: string): string =>
        `${name} will be notified and asked to upload a corrected certificate. The booking stays exactly where it is.`,
    insuranceRequestChangesNoteLabel: 'What needs to change',
    insuranceRequestChangesNotePlaceholder: 'Shown to the resident, e.g. Missing the HOA as additional insured',
    insuranceRequestChangesNoteRequired: 'Tell the resident what needs to change.',
    // Deliberately distinct copy from the row/card action button's own "Request changes" label
    // (which opens this modal) — two buttons reading "Request changes" on screen at once, one of
    // which actually sends it, would be genuinely ambiguous to click through, not just untestable.
    insuranceRequestChangesConfirm: 'Send to resident',
    insuranceDenyTitle: 'Deny certificate',
    insuranceDenyBody: (name: string): string =>
        `${name}'s booking is cancelled and fully refunded — deny is for a real refusal, not a fixable defect. Use Request changes if the certificate can be corrected instead.`,
    insuranceDenyReasonLabel: 'Reason',
    insuranceDenyReasonPlaceholder: 'Shown to the resident',
    insuranceDenyReasonRequired: 'A reason is required.',
    insuranceDenyConfirm: 'Deny & cancel booking',
    insuranceApproved: (name: string): string => `Approved — ${name}'s certificate is on file.`,
    insuranceChangesRequestedOutcome: (name: string): string => `Changes requested — ${name} has been notified.`,
    insuranceDenied: (name: string): string => `Denied — ${name}'s booking is cancelled and refunded.`,
    insuranceAiRerunQueued: 'Re-running the AI check…',
    insuranceAlreadyDecided: 'That review was already decided. The queue below is up to date.',
    insuranceDocumentRequired: "There's nothing to decide yet — the resident hasn't uploaded a certificate.",
    // Detail modal (row click): extracted fields · issues · checked-against · document viewer.
    insuranceDetailTitle: 'Insurance review',
    insuranceDetailDocumentTitle: 'Document',
    insuranceDetailExtractedTitle: 'Extracted from the document',
    insuranceDetailFieldInsured: 'Insured name',
    insuranceDetailFieldInsurer: 'Insurer',
    insuranceDetailFieldPolicyNumber: 'Policy number',
    insuranceDetailFieldEffectiveDate: 'Effective date',
    insuranceDetailFieldExpiryDate: 'Expiry date',
    insuranceDetailFieldCertificateHolder: 'Certificate holder',
    insuranceDetailFieldAdditionalInsured: 'Additional insured',
    insuranceDetailIssuesTitle: 'Issues found',
    insuranceDetailNoIssues: 'No issues found.',
    insuranceDetailCheckedAgainstTitle: 'Checked against',
    insuranceDetailNoDocument: 'No document uploaded yet.',
    insuranceDetailDocumentLoadError: "Couldn't load the document.",
    insuranceDetailNotACertificate: 'The AI could not confirm this is a certificate of insurance — review the document directly.',
    // ----- Bookings tab: per-booking insurance chip + filter -----
    // AUTOCANCEL_SPEC.md C3 — what an auto-cancelled (INSURANCE_NOT_PROVIDED) booking reads
    // wherever a cancel reason is displayed. Em dash on purpose, the spec's exact words.
    bookingCancelledNoCertificate: 'Cancelled — no certificate',
    bookingsInsuranceFilterLabel: 'Insurance',
    bookingsInsuranceFilterAll: 'All',
    // ----- Amenity list: "Insurance" tag when required -----
    insuranceRequiredTag: 'Insurance',
};
